/**
 * FoundationDispatchHistory — read-only Foundation dispatch history
 * surface for the Client detail page.
 *
 * Pulls from /api/clients/{id}/foundation-dispatches (iter164 route)
 * which queries the local Mongo cache populated by the bridge sync.
 *
 * Rendered as a collapsible table inside the Client detail modal:
 *   - Compact rows: dispatch_number | date | job_number | status | total_revenue
 *   - Click a row → expands inline to show its billing line items
 *   - Every column maps to a real Foundation column observed in
 *     iter163's live probe; no decoration / no inferred data
 *
 * Empty states:
 *   - Client has no customer_number → friendly nudge "assign a Foundation #"
 *   - Client has a customer_number but no Foundation rows → "No history yet"
 *   - API error → silent fail (component returns null) — surface noise
 *     isn't worth it on a side panel
 */
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { getClientFoundationDispatches } from "@/lib/api";

const fmtMoney = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD" })
    : "—";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return String(iso); }
};

const fmtFoundationId = (v) =>
  typeof v === "string" ? v.trim() : v;

function BillingRow({ b }) {
  return (
    <tr className="bg-slate-50/50" data-testid="foundation-billing-row">
      <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{fmtFoundationId(b.type)}</td>
      <td className="px-3 py-1.5 text-xs text-slate-700">{b.description || "—"}</td>
      <td className="px-3 py-1.5 text-right text-xs tabular-nums">{b.quantity ?? "—"}</td>
      <td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-500">{fmtMoney(b.cost)}</td>
      <td className="px-3 py-1.5 text-right text-xs tabular-nums font-medium text-emerald-700">{fmtMoney(b.amount)}</td>
    </tr>
  );
}

function DispatchRow({ d }) {
  const [open, setOpen] = useState(false);
  const billings = d.billings || [];
  return (
    <>
      <tr className="hover:bg-slate-50 cursor-pointer"
          onClick={() => setOpen(o => !o)}
          data-testid={`foundation-dispatch-row-${fmtFoundationId(d.dispatch_number)}`}>
        <td className="px-3 py-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
        </td>
        <td className="px-3 py-2 font-mono text-xs text-slate-700">{fmtFoundationId(d.dispatch_number)}</td>
        <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(d.date_called)}</td>
        <td className="px-3 py-2 font-mono text-xs text-slate-500">{fmtFoundationId(d.job_number)}</td>
        <td className="px-3 py-2 font-mono text-xs text-slate-500">{fmtFoundationId(d.site_number)}</td>
        <td className="px-3 py-2 text-xs">
          <span className={
            d.record_status === "A"
              ? "px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-semibold"
              : d.record_status === "V"
                ? "px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-semibold"
                : "px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-semibold"
          }>
            {d.record_status || "—"}
          </span>
        </td>
        <td className="px-3 py-2 text-right text-xs tabular-nums text-slate-500">{billings.length}</td>
        <td className="px-3 py-2 text-right text-xs tabular-nums font-semibold">{fmtMoney(d.total_revenue)}</td>
      </tr>
      {open && billings.length > 0 && (
        <tr>
          <td colSpan={8} className="bg-slate-50 px-3 py-2"
              data-testid={`foundation-dispatch-billings-${fmtFoundationId(d.dispatch_number)}`}>
            <div className="overflow-hidden rounded-md border border-slate-200">
              <table className="w-full">
                <thead className="bg-slate-100/60">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-1.5">Type</th>
                    <th className="px-3 py-1.5">Description</th>
                    <th className="px-3 py-1.5 text-right">Qty</th>
                    <th className="px-3 py-1.5 text-right">Cost</th>
                    <th className="px-3 py-1.5 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {billings.map((b, i) => (
                    // eslint-disable-next-line react/no-array-index-key -- Foundation pushed billings are append-only and ordered; no stable id surfaced in the response
                    <BillingRow key={`bill-${i}`} b={b} />
                  ))}
                </tbody>
                <tfoot className="bg-slate-100/40 font-semibold">
                  <tr className="text-xs">
                    <td className="px-3 py-1.5" colSpan={3}>Totals</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtMoney(d.total_cost)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{fmtMoney(d.total_revenue)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </td>
        </tr>
      )}
      {open && billings.length === 0 && (
        <tr>
          <td colSpan={8} className="bg-slate-50 px-3 py-3 text-center text-xs text-slate-400"
              data-testid={`foundation-dispatch-empty-${fmtFoundationId(d.dispatch_number)}`}>
            No billing line items recorded against this dispatch.
          </td>
        </tr>
      )}
    </>
  );
}

export default function FoundationDispatchHistory({ clientId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getClientFoundationDispatches(clientId, 50)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) {
    return (
      <div className="py-8 text-center text-sm text-slate-400" data-testid="foundation-history-loading">
        <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading Foundation history…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="foundation-dispatch-history">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-sm font-semibold text-slate-700">Foundation History</span>
        {data.customer_number && (
          <span className="font-mono text-xs text-slate-400">cn #{data.customer_number}</span>
        )}
        <span className="ml-auto text-xs text-slate-400">
          {data.dispatches?.length || 0} record{(data.dispatches?.length || 0) === 1 ? "" : "s"}
        </span>
      </div>

      {!data.customer_number && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800"
             data-testid="foundation-history-no-cn">
          {data.note || "Assign a Foundation customer number to surface dispatch history."}
        </div>
      )}

      {data.customer_number && (data.dispatches || []).length === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-500 text-center"
             data-testid="foundation-history-empty">
          No Foundation dispatches recorded for customer #{data.customer_number}.
        </div>
      )}

      {(data.dispatches || []).length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 w-6"></th>
                <th className="px-3 py-2">Dispatch #</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Job #</th>
                <th className="px-3 py-2">Site #</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Lines</th>
                <th className="px-3 py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.dispatches.map(d => (
                <DispatchRow key={fmtFoundationId(d.dispatch_number)} d={d} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
